import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Tag, Search, RefreshCw, Upload, Download } from 'lucide-react';
import api, { assetsApi, mastersApi, reportsApi } from '../services/api';
import { Table, Pagination, Modal, Field, Spinner } from '../components/common';
import AssetSearchInput from '../components/common/AssetSearchInput';
import BulkUploadModal from '../components/common/BulkUploadModal';
import { fmt, useDebounce, StatusBadge } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const downloadBlobTemplate = async (type, filename) => {
  try {
    const res = await api.get(`/templates/download/${type}`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  } catch { toast.error('Download failed'); }
};

const txApi = {
  mapTag:      d  => api.post('/tags/map', d).then(r => r.data),
  lookupTag:   v  => api.get(`/tags/lookup/${encodeURIComponent(v)}`).then(r => r.data),
  disposals:   p  => api.get('/transactions/disposals', { params: p }).then(r => r.data),
  createDisposal: d => api.post('/transactions/disposals', d).then(r => r.data),
  actionDisposal: (id, d) => api.patch(`/transactions/disposals/${id}/action`, d).then(r => r.data),
  checkouts:   p  => api.get('/transactions/checkouts', { params: p }).then(r => r.data),
  checkout:    d  => api.post('/transactions/checkouts', d).then(r => r.data),
  checkin:     id => api.patch(`/transactions/checkouts/${id}/checkin`).then(r => r.data),
};

// ─── Tag Mapping ─────────────────────────────────────────
export function TagMapping() {
  const [assets,    setAssets]    = useState({ data: [], pagination: {} });
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(1);
  const [search,    setSearch]    = useState('');
  const [showMap,   setShowMap]   = useState(false);
  const [showBulk,  setShowBulk]  = useState(false);
  const [lookupVal, setLookupVal] = useState('');
  const [lookupRes, setLookupRes] = useState(null);
  const [lookupErr, setLookupErr] = useState('');
  const debSearch = useDebounce(search, 400);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await assetsApi.list({ page, limit: 25, search: debSearch || undefined }); setAssets(r); }
    catch {} finally { setLoading(false); }
  }, [page, debSearch]);

  useEffect(() => { load(); }, [load]);

  const handleLookup = async () => {
    if (!lookupVal.trim()) return;
    setLookupRes(null); setLookupErr('');
    try { const r = await txApi.lookupTag(lookupVal.trim()); setLookupRes(r.data); }
    catch (err) {
      if (err.response?.status === 404) setLookupErr('Tag not found in system');
      else setLookupErr('Lookup failed');
    }
  };

  const cols = [
    { key: 'asset_code', title: 'Asset Code', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: 'var(--fs-xs)' }}>{v}</span> },
    { key: 'asset_name', title: 'Asset Name', render: v => fmt.truncate(v, 40) },
    { key: 'location_name', title: 'Location', render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'is_tagged',  title: 'Tag Status', render: (v, r) => v
      ? <span className={`badge ${r.tag_type==='rfid'?'badge-purple':r.tag_type==='qr'?'badge-blue':'badge-teal'}`}><Tag size={9}/> {(r.tag_type||'').toUpperCase()}</span>
      : r.is_taggable === false ? <span className="badge badge-gray">Not taggable</span>
      : <span className="badge badge-red">Untagged</span> },
    { key: 'asset_status', title: 'Status',  render: v => <StatusBadge status={v} /> },
    { key: 'id', title: '', align: 'right', render: (_, r) => (
      <button className="btn btn-secondary btn-sm" onClick={() => setShowMap(r)}><Tag size={12}/> Map Tag</button>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Tag Mapping</h1><p className="page-sub">Map RFID / QR Code / Barcode to asset records</p></div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => downloadBlobTemplate('tag_mapping', 'Tag_Mapping_Template.xlsx')}><Download size={13}/> Download Template</button>
          <button className="btn btn-secondary" onClick={() => setShowBulk(true)}><Upload size={13}/> Bulk Upload</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, marginBottom: 20, alignItems: 'start' }}>
        <div className="card">
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-light)', display: 'flex', gap: 10 }}>
            <div className="search-wrap" style={{ flex: 1 }}><Search size={14}/><input className="search-input" placeholder="Search assets…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}/></div>
            <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button>
          </div>
          <Table columns={cols} data={assets.data} loading={loading} emptyText="No assets found" />
          {assets.pagination?.total > 0 && <Pagination page={page} totalPages={assets.pagination.totalPages || 1} total={assets.pagination.total} limit={25} onPage={setPage} />}
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Tag Lookup</span></div>
          <div className="card-body">
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 10 }}>Scan or enter a tag value to find the mapped asset.</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input className="form-control" placeholder="Scan / enter RFID, QR, barcode…" value={lookupVal} onChange={e => setLookupVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLookup()} />
              <button className="btn btn-primary btn-sm" onClick={handleLookup}><Search size={13}/></button>
            </div>
            {lookupErr && <div className="alert alert-danger" style={{ marginBottom: 8 }}><span style={{ fontSize: 'var(--fs-xs)' }}>{lookupErr}</span></div>}
            {lookupRes && (
              <div style={{ background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--primary)', fontFamily: 'monospace', marginBottom: 6 }}>{lookupRes.asset_code}</div>
                <div style={{ fontSize: 'var(--fs-sm)', marginBottom: 8 }}>{lookupRes.asset_name}</div>
                {[['Location', lookupRes.location_name], ['Department', lookupRes.department_name], ['Custodian', lookupRes.custodian_name], ['Tag Type', lookupRes.tag_type]].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-xs)', marginBottom: 3 }}>
                    <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v || '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showMap && <MapTagModal asset={showMap} onClose={() => setShowMap(false)} onSuccess={() => { setShowMap(false); load(); }} />}
      <BulkUploadModal open={showBulk} onClose={() => setShowBulk(false)} onSuccess={load} type="tag_mapping" title="Tag Mapping" />
    </div>
  );
}

function MapTagModal({ asset, onClose, onSuccess }) {
  const [form, setForm] = useState({ tag_type: 'rfid', asset_id: asset.id });
  const [loading, setLoading] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.tag_value) { toast.error('Tag value required'); return; }
    setLoading(true);
    try { await txApi.mapTag(form); toast.success('Tag mapped successfully'); onSuccess(); }
    catch { /* message already shown by the API error handler (services/api.js) */ }
    finally { setLoading(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title={`Map Tag — ${asset.asset_code}`} size="sm"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <Spinner size="sm"/> : 'Map Tag'}</button></>}>
      <div style={{ background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: 12, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--fs-sm)', color: 'var(--primary)' }}>{asset.asset_code}</div>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{asset.asset_name}</div>
      </div>
      <Field label="Tag Type" required>
        <select className="form-control" value={form.tag_type} onChange={set('tag_type')}>
          <option value="rfid">RFID (UHF Gen2)</option>
          <option value="qr">QR Code</option>
          <option value="barcode">Barcode</option>
        </select>
      </Field>
      <Field label="Tag Value / EPC" required hint={form.tag_type === 'rfid' ? '24-char EPC hex e.g. E2801160600002023B85B48A' : form.tag_type === 'qr' ? 'QR code payload string' : 'Barcode number'}>
        <input className="form-control" value={form.tag_value || ''} onChange={set('tag_value')} placeholder="Scan or type…" autoFocus />
      </Field>
      <div className="form-row cols-2">
        <Field label="Tag Format"><input className="form-control" value={form.tag_format || ''} onChange={set('tag_format')} placeholder="EPC96, Code128…" /></Field>
        <Field label="Material">
          <select className="form-control" value={form.tag_material || ''} onChange={set('tag_material')}>
            <option value="">Select…</option>
            <option value="polyester">Polyester</option>
            <option value="metal">Metal on-metal</option>
            <option value="destructive">Destructive/Void</option>
            <option value="paper">Paper</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}

// ─── Disposals ────────────────────────────────────────────
export function Disposals() {
  const { can } = useAuth();
  const [data,    setData]    = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [showCreate, setCreate] = useState(false);
  const [showBulk,   setBulk]  = useState(false);
  const [acting,     setActing] = useState(null); // disposal id currently being approved/cancelled

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await txApi.disposals({ page, limit: 25 }); setData(r); }
    catch {} finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    setActing(id);
    try {
      await txApi.actionDisposal(id, { action });
      toast.success(action === 'approved' ? 'Disposal approved' : 'Disposal cancelled');
      load();
    } catch { /* message already shown by the API error handler (services/api.js) */ }
    finally { setActing(null); }
  };

  const canApprove = can('transactions:disposals:approve');

  const cols = [
    { key: 'disposal_no',    title: 'Disposal No', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 'var(--fs-xs)' }}>{v}</span> },
    { key: 'asset_code',     title: 'Asset',       render: (v, r) => <><span style={{ fontWeight: 500 }}>{v}</span><br/><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmt.truncate(r.asset_name, 28)}</span></> },
    { key: 'disposal_type',  title: 'Type',        render: v => <span className={`badge ${v === 'scrap' ? 'badge-red' : v === 'sale' ? 'badge-green' : 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{v}</span> },
    { key: 'disposal_date',  title: 'Date',        render: v => fmt.date(v) },
    { key: 'nbv_at_disposal',title: 'NBV',         align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-xs)' }}>{fmt.currency(v)}</span> },
    { key: 'sale_value',     title: 'Sale Value',  align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-xs)', color: 'var(--accent)' }}>{fmt.currency(v)}</span> },
    { key: 'status',         title: 'Status',      render: v => <StatusBadge status={v} /> },
    { key: 'id',             title: '',            align: 'right', render: (id, r) => r.status !== 'pending' || !canApprove ? null : (
      <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
        <button className="btn btn-secondary btn-sm" disabled={acting === id} onClick={() => handleAction(id, 'cancelled')}>Reject</button>
        <button className="btn btn-primary btn-sm" disabled={acting === id} onClick={() => handleAction(id, 'approved')}>{acting === id ? <Spinner size="sm"/> : 'Approve'}</button>
      </div>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Scrap / Disposal</h1><p className="page-sub">Asset write-off, scrap, and sale records</p></div>
        {can('transactions:disposals:create') && (
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={() => setBulk(true)}><Upload size={13}/> Bulk Upload</button>
            <button className="btn btn-primary"   onClick={() => setCreate(true)}><Plus size={14}/> New Disposal</button>
          </div>
        )}
      </div>
      <div className="card">
        <Table columns={cols} data={data.data} loading={loading} emptyText="No disposal records" />
        {data.pagination?.total > 0 && <Pagination page={page} totalPages={data.pagination.totalPages || 1} total={data.pagination.total} limit={25} onPage={setPage} />}
      </div>
      <DisposalModal open={showCreate} onClose={() => setCreate(false)} onSuccess={() => { setCreate(false); load(); }} />
      <BulkUploadModal open={showBulk} onClose={() => setBulk(false)} onSuccess={load} type="disposals" title="Scrap / Disposals" />
    </div>
  );
}

function DisposalModal({ open, onClose, onSuccess }) {
  const [form, setForm]     = useState({ disposal_type: 'scrap' });
  const [loading, setLoading]= useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.asset_id)     { toast.error('Select an asset'); return; }
    if (!form.disposal_type){ toast.error('Disposal type required'); return; }
    setLoading(true);
    try { await txApi.createDisposal(form); toast.success('Disposal recorded'); setForm({ disposal_type: 'scrap' }); onSuccess(); }
    catch { /* message already shown by the API error handler (services/api.js) */ }
    finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Record Disposal / Scrap" size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <Spinner size="sm"/> : 'Save'}</button></>}>
      <div className="alert alert-warning" style={{ marginBottom: 16 }}><span>This permanently changes the asset status and removes it from active inventory.</span></div>
      <Field label="Asset" required>
        <AssetSearchInput value={form.asset_id || ''} onChange={(id) => setForm(f => ({ ...f, asset_id: id }))} />
      </Field>
      <div className="form-row cols-2">
        <Field label="Disposal Type" required>
          <select className="form-control" value={form.disposal_type} onChange={set('disposal_type')}>
            <option value="scrap">Scrap</option>
            <option value="sale">Sale</option>
            <option value="write_off">Write-off</option>
            <option value="donation">Donation</option>
          </select>
        </Field>
        <Field label="Disposal Date"><input className="form-control" type="date" value={form.disposal_date || ''} onChange={set('disposal_date')} /></Field>
      </div>
      {form.disposal_type === 'sale' && (
        <div className="form-row cols-2">
          <Field label="Sale Value (₹)"><input className="form-control" type="number" value={form.sale_value || ''} onChange={set('sale_value')} placeholder="0" /></Field>
          <Field label="Buyer Name"><input className="form-control" value={form.buyer_name || ''} onChange={set('buyer_name')} /></Field>
        </div>
      )}
      <Field label="Reason / Remarks"><textarea className="form-control" rows={2} value={form.reason || ''} onChange={set('reason')} /></Field>
    </Modal>
  );
}

// ─── Checkouts ────────────────────────────────────────────
export function Checkouts() {
  const [data,    setData]    = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [tab,     setTab]     = useState('checked_out');
  const [showCreate, setCreate]= useState(false);
  const [showBulk,   setBulk]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await txApi.checkouts({ page, limit: 25, status: tab === 'all' ? undefined : tab }); setData(r); }
    catch {} finally { setLoading(false); }
  }, [page, tab]);

  useEffect(() => { load(); }, [load]);

  const handleCheckin = async (id) => {
    try { await txApi.checkin(id); toast.success('Asset checked in'); load(); }
    catch {}
  };

  const cols = [
    { key: 'checkout_no',    title: 'Checkout No',  render: v => <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 'var(--fs-xs)' }}>{v}</span> },
    { key: 'asset_code',     title: 'Asset',        render: (v, r) => <><span style={{ fontWeight: 500 }}>{v}</span><br/><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmt.truncate(r.asset_name, 28)}</span></> },
    { key: 'issued_to_name', title: 'Issued To',    render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'checkout_date',  title: 'Issued On',    render: v => fmt.date(v) },
    { key: 'expected_return',title: 'Expected Back', render: v => <span style={{ fontSize: 'var(--fs-xs)', color: v && new Date(v) < new Date() ? 'var(--danger)' : 'var(--text-secondary)' }}>{fmt.date(v)}</span> },
    { key: 'purpose',        title: 'Purpose',      render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{fmt.truncate(v, 30)}</span> },
    { key: 'status',         title: 'Status',       render: v => <StatusBadge status={v} /> },
    { key: 'id', title: '', align: 'right', render: (_, r) => r.status === 'checked_out' && (
      <button className="btn btn-success btn-sm" onClick={() => handleCheckin(r.id)}><RefreshCw size={12}/> Check In</button>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Check-In / Check-Out</h1><p className="page-sub">Asset lending and return tracking</p></div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => setBulk(true)}><Upload size={13}/> Bulk Upload</button>
          <button className="btn btn-primary"   onClick={() => setCreate(true)}><Plus size={14}/> Issue Asset</button>
        </div>
      </div>
      <div className="tabs">
        {[{ k:'checked_out',l:'Checked Out'},{k:'returned',l:'Returned'},{k:'overdue',l:'Overdue'},{k:'all',l:'All'}].map(t => (
          <button key={t.k} className={`tab-btn${tab === t.k ? ' active' : ''}`} onClick={() => { setTab(t.k); setPage(1); }}>{t.l}</button>
        ))}
      </div>
      <div className="card">
        <Table columns={cols} data={data.data} loading={loading} emptyText="No checkout records" />
        {data.pagination?.total > 0 && <Pagination page={page} totalPages={data.pagination.totalPages || 1} total={data.pagination.total} limit={25} onPage={setPage} />}
      </div>
      <CheckoutModal open={showCreate} onClose={() => setCreate(false)} onSuccess={() => { setCreate(false); load(); }} />
      <BulkUploadModal open={showBulk} onClose={() => setBulk(false)} onSuccess={load} type="checkouts" title="Check-Outs" />
    </div>
  );
}

function CheckoutModal({ open, onClose, onSuccess }) {
  const [form, setForm]       = useState({ condition_out: 'good' });
  const [loading, setLoading] = useState(false);
  const [custodians, setCusts]= useState([]);
  useEffect(() => { if (!open) return; mastersApi.custodians({ limit: 500 }).then(r => setCusts(r.data || [])); }, [open]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.asset_id) { toast.error('Select an asset'); return; }
    if (!form.issued_to){ toast.error('Select custodian'); return; }
    setLoading(true);
    try { await txApi.checkout(form); toast.success('Asset issued'); setForm({ condition_out: 'good' }); onSuccess(); }
    catch { /* message already shown by the API error handler (services/api.js) */ }
    finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Issue / Check Out Asset" size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <Spinner size="sm"/> : 'Issue Asset'}</button></>}>
      <Field label="Asset" required>
        <AssetSearchInput value={form.asset_id || ''} onChange={(id) => setForm(f => ({ ...f, asset_id: id }))} status="active" />
      </Field>
      <Field label="Issue To (Custodian)" required>
        <select className="form-control" value={form.issued_to || ''} onChange={set('issued_to')}>
          <option value="">Select custodian…</option>
          {custodians.map(c => <option key={c.id} value={c.id}>{c.full_name}{c.employee_id ? ` (${c.employee_id})` : ''} — {c.department_name || '—'}</option>)}
        </select>
      </Field>
      <div className="form-row cols-2">
        <Field label="Expected Return"><input className="form-control" type="date" value={form.expected_return || ''} onChange={set('expected_return')} /></Field>
        <Field label="Condition">
          <select className="form-control" value={form.condition_out} onChange={set('condition_out')}>
            <option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option>
          </select>
        </Field>
      </div>
      <Field label="Purpose"><textarea className="form-control" rows={2} value={form.purpose || ''} onChange={set('purpose')} /></Field>
    </Modal>
  );
}

// ─── Custodians ────────────────────────────────────────────
export function Custodians() {
  const [data,    setData]    = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [showForm,setShowForm]= useState(false);
  const [editRow, setEditRow] = useState(null);
  const [showBulk,setBulk]    = useState(false);
  const debSearch = useDebounce(search, 400);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await mastersApi.custodians({ page, limit: 50, search: debSearch || undefined }); setData(r); }
    catch {} finally { setLoading(false); }
  }, [page, debSearch]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'full_name',       title: 'Name',        render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'employee_id',     title: 'Employee ID', render: v => <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'department_name', title: 'Department' },
    { key: 'designation',     title: 'Designation', render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'email',           title: 'Email',       render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'phone',           title: 'Phone' },
    { key: 'asset_count',     title: 'Assets',      align: 'right', render: v => <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{fmt.number(v || 0)}</span> },
    { key: 'id', title: '', align: 'right', render: (_, r) => <button className="btn btn-ghost btn-sm" onClick={() => { setEditRow(r); setShowForm(true); }}>Edit</button> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Custodians</h1><p className="page-sub">Employees responsible for assets</p></div>
        <div className="page-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => downloadBlobTemplate('custodians', 'Custodians_Template.xlsx')}><Download size={13}/> Template</button>
          <button className="btn btn-secondary"  onClick={() => setBulk(true)}><Upload size={13}/> Bulk Upload</button>
          <button className="btn btn-primary"    onClick={() => { setEditRow(null); setShowForm(true); }}><Plus size={14}/> Add Custodian</button>
        </div>
      </div>
      <div className="card">
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-light)' }}>
          <input className="search-input" style={{ maxWidth: 260 }} placeholder="Search custodians…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Table columns={cols} data={data.data} loading={loading} emptyText="No custodians yet" />
        {data.pagination?.total > 0 && <Pagination page={page} totalPages={data.pagination.totalPages || 1} total={data.pagination.total} limit={50} onPage={setPage} />}
      </div>
      <CustodianModal open={showForm} onClose={() => setShowForm(false)} editData={editRow} onSuccess={() => { setShowForm(false); load(); }} />
      <BulkUploadModal open={showBulk} onClose={() => setBulk(false)} onSuccess={load} type="custodians" title="Custodians" />
    </div>
  );
}

function CustodianModal({ open, onClose, editData, onSuccess }) {
  const [form, setForm]       = useState({});
  const [loading, setLoading] = useState(false);
  const [departments, setDepts] = useState([]);
  useEffect(() => { setForm(editData || {}); mastersApi.departments({ limit: 200 }).then(r => setDepts(r.data || [])); }, [editData]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.full_name) { toast.error('Full name required'); return; }
    setLoading(true);
    try {
      editData ? await mastersApi.updateCustodian(editData.id, form) : await mastersApi.createCustodian(form);
      toast.success('Saved'); onSuccess();
    } catch {} finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={editData ? 'Edit Custodian' : 'Add Custodian'} size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <Spinner size="sm"/> : 'Save'}</button></>}>
      <div className="form-row cols-2">
        <Field label="Full Name" required><input className="form-control" value={form.full_name || ''} onChange={set('full_name')} /></Field>
        <Field label="Employee ID"><input className="form-control" value={form.employee_id || ''} onChange={set('employee_id')} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Email"><input className="form-control" type="email" value={form.email || ''} onChange={set('email')} /></Field>
        <Field label="Phone"><input className="form-control" value={form.phone || ''} onChange={set('phone')} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Department"><select className="form-control" value={form.department_id || ''} onChange={set('department_id')}><option value="">Select…</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
        <Field label="Designation"><input className="form-control" value={form.designation || ''} onChange={set('designation')} /></Field>
      </div>
    </Modal>
  );
}

// ─── Company Hierarchy ─────────────────────────────────────
export function CompanyHierarchy() {
  const { CompanyHierarchy: CH } = require('./CompanyMaster');
  return <CH />;
}
